
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

const INSTAGRAM_URL = 'https://www.instagram.com/t.yokota0809/';
const LINE_URL = 'https://line.me/ti/p/9_7o9hrucX';

const LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'ja', label: '日本語', short: 'JP' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isCtaOpen, setIsCtaOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const servicesRef = useRef<HTMLDivElement>(null);

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setIsLangOpen(false);
      }
      if (ctaRef.current && !ctaRef.current.contains(e.target as Node)) {
        setIsCtaOpen(false);
      }
      if (servicesRef.current && !servicesRef.current.contains(e.target as Node)) {
        setIsServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToSection = (id: string) => {
    if (!isHomePage) {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    navigate('/');
    if (isHomePage) window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    setIsLangOpen(false);
    setIsMobileMenuOpen(false);
  };

  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  const navLinkClass = isScrolled
    ? 'text-[#3d1f0a] hover:text-[#9B2335] transition-colors duration-200 text-sm whitespace-nowrap'
    : 'text-white hover:text-[#f5d98a] transition-colors duration-200 text-sm whitespace-nowrap drop-shadow-lg';

  const isJa = i18n.language === 'ja';

  const serviceItems = [
    {
      key: 'driving',
      icon: 'ri-steering-2-line',
      label: isJa ? '運転代行' : 'Driver Service',
      sub: isJa ? 'レンタカー運転・観光送迎' : 'Rental car & sightseeing',
      action: () => { navigate(isJa ? '/driving-assistant' : '/driving-assistant-en'); setIsServicesOpen(false); setIsMobileMenuOpen(false); },
    },
    {
      key: 'management',
      icon: 'ri-building-4-line',
      label: isJa ? '民泊管理' : 'Minpaku Mgmt',
      sub: isJa ? '運営代行・清掃・サポート' : 'Operations & cleaning',
      action: () => { navigate('/management'); setIsServicesOpen(false); setIsMobileMenuOpen(false); },
    },
    {
      key: 'owners',
      icon: 'ri-home-heart-line',
      label: isJa ? 'オーナー向け' : 'For Owners',
      sub: isJa ? '物件オーナー様へのご提案' : 'Proposals for property owners',
      action: () => { navigate('/for-owners'); setIsServicesOpen(false); setIsMobileMenuOpen(false); },
    },
    {
      key: 'pricing',
      icon: 'ri-price-tag-3-line',
      label: isJa ? '料金プラン' : 'Pricing',
      sub: isJa ? '各サービスの料金一覧' : 'Service pricing details',
      action: () => { navigate('/pricing'); setIsServicesOpen(false); setIsMobileMenuOpen(false); },
    },
    {
      key: 'minpaku-registration',
      icon: 'ri-file-list-3-line',
      label: isJa ? '民泊登録サポート' : 'Minpaku Registration',
      sub: isJa ? '民泊申請・許可取得サポート' : 'Registration & permit support',
      action: () => { navigate('/minpaku-registration'); setIsServicesOpen(false); setIsMobileMenuOpen(false); },
    },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'shadow-md border-b-2 border-[#9B2335]' : 'bg-transparent'
      }`}
      style={
        isScrolled
          ? { background: 'linear-gradient(to bottom, #F5E6C8, #EDD9A3)', fontFamily: "'Noto Serif JP', serif" }
          : { fontFamily: "'Noto Serif JP', serif" }
      }
    >
      {isScrolled && (
        <div className="w-full h-1.5 flex overflow-hidden">
          {Array.from({ length: 80 }).map((_, i) => (
            <div key={i} className="flex-1 h-full" style={{ backgroundColor: i % 2 === 0 ? '#9B2335' : '#C8960C' }} />
          ))}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* ロゴ */}
          <div className="flex items-center cursor-pointer" onClick={handleLogoClick}>
            <img
              src="https://public.readdy.ai/ai/img_res/de640276-e1f2-4aaf-8379-d8e42afbd5f8.png"
              alt="KOMINKA - Showa Retro Traditional House in Tokyo"
              className="h-12 w-auto"
            />
          </div>

          {/* デスクトップメニュー */}
          <div className="hidden md:flex items-center space-x-6">
            {[
              { key: 'home', label: t('common:nav.home'), action: () => scrollToSection('home') },
              { key: 'rooms', label: t('common:nav.rooms'), action: () => scrollToSection('rooms') },
              { key: 'access', label: t('common:nav.access'), action: () => scrollToSection('access') },
              { key: 'reservation', label: t('common:nav.reservation'), action: () => scrollToSection('reservation') },
            ].map((item) => (
              <button
                key={item.key}
                onClick={item.action}
                className={`${navLinkClass} cursor-pointer relative group`}
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300" style={{ backgroundColor: isScrolled ? '#9B2335' : '#f5d98a' }} />
              </button>
            ))}

            {/* サービス ドロップダウン */}
            <div className="relative" ref={servicesRef}>
              <button
                onClick={() => setIsServicesOpen(!isServicesOpen)}
                className={`${navLinkClass} cursor-pointer relative group flex items-center gap-1`}
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                {isJa ? 'サービス' : 'Services'}
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`}></i>
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300" style={{ backgroundColor: isScrolled ? '#9B2335' : '#f5d98a' }} />
              </button>

              {isServicesOpen && (
                <div className="absolute left-0 top-full mt-2 w-60 bg-white border shadow-xl rounded-md overflow-hidden z-50" style={{ borderColor: '#c8a96e' }}>
                  {serviceItems.map((item) => (
                    <button
                      key={item.key}
                      onClick={item.action}
                      className="flex items-center gap-3 px-4 py-3.5 text-sm hover:bg-[#F5E6C8] transition-colors duration-150 cursor-pointer border-b last:border-b-0 w-full text-left"
                      style={{ fontFamily: "'Noto Serif JP', serif", color: '#3d1f0a', borderColor: 'rgba(200,169,110,0.3)' }}
                    >
                      <span className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0" style={{ background: 'rgba(139,37,0,0.1)', color: '#8B2500' }}>
                        <i className={`${item.icon} text-sm`}></i>
                      </span>
                      <div>
                        <div className="font-semibold text-xs">{item.label}</div>
                        <div className="text-[10px] opacity-60" style={{ color: '#6b4c2a' }}>{item.sub}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => window.open(INSTAGRAM_URL, '_blank')}
              className={`${navLinkClass} cursor-pointer relative group`}
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              Instagram
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300" style={{ backgroundColor: isScrolled ? '#9B2335' : '#f5d98a' }} />
            </button>

            {/* CTA ドロップダウン */}
            <div className="relative ml-4" ref={ctaRef}>
              <button
                onClick={() => setIsCtaOpen(!isCtaOpen)}
                className="flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-md border hover:shadow-lg hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{
                  background: isScrolled
                    ? 'linear-gradient(135deg, #8B2500 0%, #c8504a 100%)'
                    : 'linear-gradient(135deg, #c8504a 0%, #e85d54 100%)',
                  borderColor: isScrolled ? '#6b1c00' : 'rgba(255,255,255,0.3)',
                }}
              >
                {isJa ? '予約する' : 'Book Now'}
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${isCtaOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isCtaOpen && (
                <div className="absolute right-0 top-full mt-2 w-52 bg-white border shadow-xl rounded-md overflow-hidden z-50" style={{ borderColor: '#c8a96e' }}>
                  <button
                    onClick={() => { scrollToSection('reservation'); setIsCtaOpen(false); }}
                    className="flex items-center gap-3 px-4 py-3.5 text-sm hover:bg-[#F5E6C8] transition-colors duration-150 cursor-pointer border-b w-full text-left"
                    style={{ fontFamily: "'Noto Serif JP', serif", color: '#3d1f0a', borderColor: 'rgba(200,169,110,0.3)' }}
                  >
                    <span className="w-7 h-7 flex items-center justify-center rounded-full" style={{ background: 'rgba(139,37,0,0.1)', color: '#8B2500' }}>
                      <i className="ri-calendar-check-line text-sm"></i>
                    </span>
                    <div>
                      <div className="font-semibold text-xs">{isJa ? '直接予約' : 'Book Direct'}</div>
                      <div className="text-[10px] opacity-70" style={{ color: '#6b4c2a' }}>{isJa ? '最安値保証' : 'Best Price'}</div>
                    </div>
                  </button>
                  <a
                    href={LINE_URL}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    onClick={() => setIsCtaOpen(false)}
                    className="flex items-center gap-3 px-4 py-3.5 text-sm hover:bg-[#F5E6C8] transition-colors duration-150 cursor-pointer"
                    style={{ fontFamily: "'Noto Serif JP', serif", color: '#3d1f0a' }}
                  >
                    <span className="w-7 h-7 flex items-center justify-center rounded-full bg-[#06C755]/10 text-[#06C755]">
                      <i className="ri-line-fill text-sm"></i>
                    </span>
                    <div>
                      <div className="font-semibold text-xs">{isJa ? 'LINE で相談' : 'LINE Inquiry'}</div>
                      <div className="text-[10px] opacity-70" style={{ color: '#6b4c2a' }}>{isJa ? '気軽にメッセージ' : 'Quick Message'}</div>
                    </div>
                  </a>
                </div>
              )}
            </div>

            {/* 言語切替 */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-all whitespace-nowrap cursor-pointer border ${
                  isScrolled
                    ? 'text-[#F5E6C8] border-[#C8960C]'
                    : 'bg-white/20 text-white border-white/40 hover:bg-white/30 backdrop-blur-sm'
                }`}
                style={{
                  fontFamily: "'Noto Serif JP', serif",
                  borderRadius: '2px',
                  ...(isScrolled ? { background: 'linear-gradient(135deg, #7A1A28 0%, #9B2335 100%)' } : {}),
                }}
              >
                <i className="ri-global-line text-xs"></i>
                <span>{currentLang.short}</span>
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${isLangOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isLangOpen && (
                <div className="absolute right-0 top-full mt-1 w-36 bg-white border shadow-lg rounded-sm overflow-hidden z-50" style={{ borderColor: 'rgba(200,169,110,0.5)' }}>
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors duration-150 cursor-pointer flex items-center gap-2 ${
                        i18n.language === lang.code ? 'font-semibold' : 'hover:bg-[#F5E6C8]'
                      }`}
                      style={{
                        fontFamily: "'Noto Serif JP', serif",
                        color: i18n.language === lang.code ? '#9B2335' : '#3d1f0a',
                        background: i18n.language === lang.code ? 'rgba(155,35,53,0.1)' : 'transparent',
                      }}
                    >
                      {i18n.language === lang.code && <i className="ri-check-line text-xs" style={{ color: '#9B2335' }}></i>}
                      {i18n.language !== lang.code && <span className="w-3"></span>}
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* モバイルメニューボタン */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 cursor-pointer ${isScrolled ? 'text-[#3d1f0a]' : 'text-white drop-shadow-lg'}`}
          >
            <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
          </button>
        </div>
      </div>

      {/* モバイルメニュー */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden shadow-lg border-t-2 border-[#9B2335]"
          style={{ background: 'linear-gradient(to bottom, #F5E6C8, #EDD9A3)', fontFamily: "'Noto Serif JP', serif" }}
        >
          <div className="w-full h-1 flex overflow-hidden">
            {Array.from({ length: 60 }).map((_, i) => (
              <div key={i} className="flex-1 h-full" style={{ backgroundColor: i % 2 === 0 ? '#9B2335' : '#C8960C' }} />
            ))}
          </div>
          <div className="px-4 py-4 space-y-1">
            {[
              { key: 'home', label: t('common:nav.home'), action: () => scrollToSection('home') },
              { key: 'rooms', label: t('common:nav.rooms'), action: () => scrollToSection('rooms') },
              { key: 'access', label: t('common:nav.access'), action: () => scrollToSection('access') },
              { key: 'reservation', label: t('common:nav.reservation'), action: () => scrollToSection('reservation') },
            ].map((item) => (
              <button
                key={item.key}
                onClick={item.action}
                className="block w-full text-left px-4 py-2.5 text-sm text-[#3d1f0a] hover:text-[#9B2335] whitespace-nowrap cursor-pointer border-b"
                style={{ fontFamily: "'Noto Serif JP', serif", borderColor: 'rgba(155,35,53,0.2)' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(200,150,12,0.12)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                ◆ {item.label}
              </button>
            ))}

            {/* モバイル サービスアコーディオン */}
            <div>
              <button
                onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                className="flex items-center justify-between w-full px-4 py-2.5 text-sm text-[#3d1f0a] hover:text-[#9B2335] cursor-pointer border-b"
                style={{ fontFamily: "'Noto Serif JP', serif", borderColor: 'rgba(155,35,53,0.2)' }}
              >
                <span>◆ {isJa ? 'サービス' : 'Services'}</span>
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${isMobileServicesOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {isMobileServicesOpen && (
                <div className="pl-4 border-b" style={{ borderColor: 'rgba(155,35,53,0.2)', background: 'rgba(200,150,12,0.06)' }}>
                  {serviceItems.map((item) => (
                    <button
                      key={item.key}
                      onClick={item.action}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-[#3d1f0a] hover:text-[#9B2335] cursor-pointer border-b last:border-b-0"
                      style={{ fontFamily: "'Noto Serif JP', serif", borderColor: 'rgba(155,35,53,0.1)' }}
                    >
                      <span className="w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0" style={{ background: 'rgba(139,37,0,0.1)', color: '#8B2500' }}>
                        <i className={`${item.icon} text-xs`}></i>
                      </span>
                      <div className="text-left">
                        <div className="text-xs font-semibold">{item.label}</div>
                        <div className="text-[10px] opacity-60">{item.sub}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => { window.open(INSTAGRAM_URL, '_blank'); setIsMobileMenuOpen(false); }}
              className="block w-full text-left px-4 py-2.5 text-sm text-[#3d1f0a] hover:text-[#9B2335] whitespace-nowrap cursor-pointer border-b"
              style={{ fontFamily: "'Noto Serif JP', serif", borderColor: 'rgba(155,35,53,0.2)' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(200,150,12,0.12)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              ◆ Instagram
            </button>

            <button
              onClick={() => { scrollToSection('reservation'); setIsMobileMenuOpen(false); }}
              className="mx-6 mt-4 mb-1 px-5 py-3 text-white text-sm font-semibold rounded-md border text-center hover:shadow-lg transition-all duration-300 block w-auto"
              style={{ background: 'linear-gradient(135deg, #8B2500 0%, #c8504a 100%)', borderColor: '#6b1c00' }}
            >
              <i className="ri-calendar-check-line mr-2"></i>
              {isJa ? '直接予約' : 'Book Direct'}
            </button>

            <a
              href={LINE_URL}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="mx-6 mb-3 px-5 py-3 bg-[#06C755] text-white text-sm font-semibold rounded-md text-center hover:shadow-lg hover:bg-[#05b34c] transition-all duration-300 block w-auto"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <i className="ri-line-fill mr-2"></i>
              {isJa ? 'LINE で相談' : 'LINE Inquiry'}
            </a>

            {/* モバイル言語切替 */}
            <div className="mx-6 mt-2 border rounded-sm overflow-hidden" style={{ borderColor: 'rgba(200,169,110,0.5)' }}>
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 cursor-pointer transition-colors duration-150 ${
                    i18n.language === lang.code ? 'font-semibold' : 'hover:bg-[#F5E6C8]'
                  }`}
                  style={{
                    fontFamily: "'Noto Serif JP', serif",
                    color: i18n.language === lang.code ? '#9B2335' : '#3d1f0a',
                    background: i18n.language === lang.code ? 'rgba(155,35,53,0.1)' : 'transparent',
                  }}
                >
                  <i className="ri-global-line text-xs"></i>
                  {lang.label}
                  {i18n.language === lang.code && <i className="ri-check-line text-xs ml-auto" style={{ color: '#9B2335' }}></i>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
